// The review family's shared payload accessors and subject/refs builders — a LEAF
// module, deliberately: `review.ts` composes `TERMINAL_RENDERERS_REVIEW_KNOWLEDGE`
// (from `./review-knowledge`, split out purely for the 800-line file cap — see that
// module's header) into its own table, so if these helpers lived in `review.ts` and
// `review-knowledge.ts` imported them from there, entering the module graph at
// `review-knowledge.ts` would run `review.ts`'s body first and hit the composed
// export's spread before that binding initialized — the exact cycle already found
// and fixed for the dispatch and maintenance families (see `dispatch-payload.ts`'s
// and `maintenance-payload.ts`'s headers). This file imports nothing from either
// review module, so neither can cycle through it.

import type { TerminalRefs, TerminalSubject } from '../../../wire/terminal';

/** A plain record view of `unknown`, or `{}` — never throws on a withheld payload. */
export function rec(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

/**
 * Named `strOr`, not `str`: `maintenance-payload.ts` exports a different `str(v,
 * key)` (reads a field BY NAME), same name/arity, opposite meaning for arg two —
 * moving a renderer between the two files under one shared name would have
 * compiled and rendered a key name as the value.
 */
export function strOr(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

export function strOpt(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export function numField(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export function numOpt(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// ─── subject builders ────────────────────────────────────────────────────────

/** A repo subject, or session when unknown — `id` is always real (`wire/terminal.ts`
 * `TerminalSubject` doc); `'unknown repo'` was a fabricated repo id, not a label. */
export function repoSubject(repo: string | null, sessionId: string): TerminalSubject {
  if (repo !== null) return { kind: 'repo', id: repo, label: repo };
  return { kind: 'session', id: sessionId, label: sessionId };
}

/** A pull-request subject when both halves are known, else the repo/session fallback. */
export function prSubject(
  repo: string | null,
  pr: number | null,
  sessionId: string,
): TerminalSubject {
  if (repo !== null && pr !== null) {
    const label = `${repo}#${pr}`;
    return { kind: 'pull_request', id: label, label };
  }
  return repoSubject(repo, sessionId);
}

/** `TerminalRefs` from whichever ids are actually known — never invents one. */
export function refsFor(input: {
  repo?: string | null;
  pr?: number | null;
  issue?: number | null;
  taskId?: string | null;
  sessionId?: string | null;
  boxId?: string | null;
}): TerminalRefs {
  return {
    ...(input.repo ? { repoFullName: input.repo } : {}),
    ...(input.pr !== undefined && input.pr !== null ? { prNumber: input.pr } : {}),
    ...(input.issue !== undefined && input.issue !== null ? { issueNumber: input.issue } : {}),
    ...(input.taskId ? { taskId: input.taskId } : {}),
    ...(input.sessionId ? { sessionId: input.sessionId } : {}),
    ...(input.boxId ? { boxId: input.boxId } : {}),
  };
}
